/**
 * Derived locale constants - computed from DEFAULT_RMS_REGION_DATA (rms-data.ts).
 * No import from locale-config.types; types are inferred locally to avoid circular dependency.
 */

import { LOCALE_FORMAT } from './locale-config.static-constants'
import { DEFAULT_RMS_REGION_DATA } from './rms-data'

// =============================================================================
// LOCAL TYPES (inferred from data - do not import from locale-config.types)
// =============================================================================

type Region = (typeof DEFAULT_RMS_REGION_DATA)[number]
type CountryCode = Lowercase<Region['regionCode']>

// =============================================================================
// DERIVED CONSTANTS (Computed from DEFAULT_RMS_REGION_DATA)
// =============================================================================

/** Supported countries array - DERIVED from DEFAULT_RMS_REGION_DATA */
export const SUPPORTED_COUNTRIES = DEFAULT_RMS_REGION_DATA.map(
  region => region.regionCode.toLowerCase()
) as readonly CountryCode[]

/** Country currency mappings - DERIVED from DEFAULT_RMS_REGION_DATA */
export const COUNTRY_CURRENCIES: Record<
  CountryCode,
  Region['defaultCurrencyIsoCode']
> = Object.fromEntries(
  DEFAULT_RMS_REGION_DATA.map(region => [
    region.regionCode.toLowerCase(),
    region.defaultCurrencyIsoCode,
  ])
) as Record<CountryCode, Region['defaultCurrencyIsoCode']>

/** Country language mappings - DERIVED from DEFAULT_RMS_REGION_DATA
 * Extracts language codes (e.g., 'en', 'fr') from locale strings (e.g., 'en-CA', 'fr-CA')
 */
export const COUNTRY_LANGUAGES: Record<CountryCode, readonly string[]> =
  Object.fromEntries(
    DEFAULT_RMS_REGION_DATA.map(region => [
      region.regionCode.toLowerCase(),
      region.supportedLanguagesIsoCodes.map(l => {
        const parts = l.toLowerCase().split('-')
        return parts[0] ?? ''
      }) as readonly string[],
    ])
  ) as unknown as Record<CountryCode, readonly string[]>

/** Default country - first entry in SUPPORTED_COUNTRIES */
const firstCountry = SUPPORTED_COUNTRIES[0]
if (!firstCountry) {
  throw new Error('SUPPORTED_COUNTRIES must have at least one entry')
}
export const DEFAULT_COUNTRY: (typeof SUPPORTED_COUNTRIES)[number] = firstCountry

/** Formats a locale string from country and language */
export function formatLocale(
  country: (typeof SUPPORTED_COUNTRIES)[number],
  language: string
): string {
  const countryStr = String(country)
  const languageStr = String(language)
  /* eslint-disable i18next/no-literal-string -- format uses template placeholders from LOCALE_FORMAT */
  const result = LOCALE_FORMAT.replace('{{country}}', countryStr).replace(
    '{{language}}',
    languageStr
  )
  /* eslint-enable i18next/no-literal-string */
  return result
}

/** Fallback locale (computed from default country and language) */
const defaultCountryLanguages = COUNTRY_LANGUAGES[DEFAULT_COUNTRY]
if (defaultCountryLanguages.length === 0) {
  throw new Error(
    `COUNTRY_LANGUAGES[${DEFAULT_COUNTRY}] must contain at least one language`
  )
}
const defaultLanguage = defaultCountryLanguages[0]
if (!defaultLanguage) {
  throw new Error(
    `COUNTRY_LANGUAGES[${DEFAULT_COUNTRY}] must have at least one language`
  )
}
export const FALLBACK_LOCALE = formatLocale(DEFAULT_COUNTRY, defaultLanguage)

// =============================================================================
// COUNTRY CONFIGURATION
// =============================================================================

/** Country configuration with flag - DERIVED from DEFAULT_RMS_REGION_DATA */
export const COUNTRY_CONFIG: Record<
  (typeof SUPPORTED_COUNTRIES)[number],
  { flag: string }
> = Object.fromEntries(
  DEFAULT_RMS_REGION_DATA.map(region => [
    region.regionCode.toLowerCase(),
    { flag: region.flag },
  ])
) as Record<(typeof SUPPORTED_COUNTRIES)[number], { flag: string }>

// =============================================================================
// DEPRECATED / EDD CONSTANTS
// =============================================================================

/** Kept for backward compatibility only */
export const ECOUNTRIES: Record<
  (typeof SUPPORTED_COUNTRIES)[number],
  (typeof SUPPORTED_COUNTRIES)[number]
> = {
  us: 'us',
  ca: 'ca',
}

/** Locales that require EDD wrapping */
export const EDD_WRAPPING_LOCALES: readonly string[] = ['en-ca', 'fr-ca']

/** Locales excluded from EDD wrapping on desktop */
export const EDD_WRAPPING_DESKTOP_EXCLUSIONS: readonly string[] = ['en-ca']

/** Locales that require EDD cutoff wrapping */
export const EDD_CUTOFF_WRAP_LOCALES: readonly string[] = ['fr-ca']
